import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import {
  generateNextNDays,
  ymdInTimezone,
  getStatusForDate,
  describeEntry,
  toJsDate,
} from '../../lib/availability-helpers.js';

// Phase 6 of v1 spec — Monday-morning confirmation popup.
//
// Trigger conditions (ALL must be true):
//   - vendor.weeklyConfirmationEnabled !== false
//   - "today" in vendor TZ is a Monday
//   - this responder hasn't confirmed for this week yet
//     (user.lastWeeklyConfirmedFor < this Monday's YYYY-MM-DD)
//
// What it shows:
//   - The 7 days of the upcoming week
//   - For each day: implicit/explicit availability status with the entry that drives it
//   - Two buttons: "Confirm" (calls confirmWeeklyShifts) and "Edit my shifts" (navigates
//     to /responder/availability)
//   - Dismiss (X) — same as Confirm, just less intentional. We still record the timestamp
//     so we don't keep re-prompting.
//
// The popup mounts inside ResponderApp's shell so it overlays both the alerts view and
// the availability page. It does NOT block urgent ringing — z-index < the alert UI.

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function WeeklyConfirmPopup() {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const myUid = user?.uid;
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [me, setMe] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false); // session-local dismiss

  // ---- Subscriptions -------------------------------------------------------------

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId),
      (snap) => setVendor(snap.exists() ? snap.data() : {}),
      () => {},
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId || !myUid) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId, 'users', myUid),
      (snap) => setMe(snap.exists() ? snap.data() : {}),
      () => {},
    );
    return () => unsub();
  }, [vendorId, myUid]);

  useEffect(() => {
    if (!vendorId || !myUid) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'vendors', vendorId, 'availability'),
        where('responderUid', '==', myUid),
      ),
      (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {},
    );
    return () => unsub();
  }, [vendorId, myUid]);

  // ---- Decide whether to show ----------------------------------------------------

  const tz = vendor?.timezone || '';
  const availabilityModel = vendor?.availabilityModel || 'opt-out';
  const featureEnabled = vendor?.weeklyConfirmationEnabled !== false; // default ON
  const todayYMD = ymdInTimezone(new Date(), tz);
  const todayDow = dayOfWeekFromYMD(todayYMD);
  const isMonday = todayDow === 1;

  // The Monday this week (always === todayYMD if it IS Monday, otherwise — but we only
  // show on Monday so we just use today's YMD).
  const thisMondayYMD = todayYMD;

  const alreadyConfirmed = (me?.lastWeeklyConfirmedFor || '') >= thisMondayYMD;

  const shouldShow = (
    !hidden
    && !!vendor
    && !!me
    && featureEnabled
    && isMonday
    && !alreadyConfirmed
  );

  // ---- Build the week preview ----------------------------------------------------

  const weekDays = useMemo(() => {
    if (!shouldShow) return [];
    const days = generateNextNDays(7);
    return days.map((date) => {
      const ymd = ymdInTimezone(date, tz);
      const status = getStatusForDate(entries, ymd, tz);
      const implicitlyAvailable = status === 'off' && availabilityModel === 'opt-out';
      return {
        ymd,
        dayName: DAY_NAMES[date.getDay()],
        dayNum: date.getDate(),
        status,
        implicitlyAvailable,
      };
    });
  }, [shouldShow, entries, tz, availabilityModel]);

  // List the entries that touch this week for the "what's planned" summary
  const weekEntries = useMemo(() => {
    if (!shouldShow) return [];
    const ymdSet = new Set(weekDays.map((d) => d.ymd));
    return entries.filter((entry) => {
      // crude but OK for v1: any entry whose date range touches this week
      const startYMD = ymdInTimezone(toJsDate(entry.startDate), tz);
      const endYMD = entry.endDate ? ymdInTimezone(toJsDate(entry.endDate), tz) : null;
      const weekFirst = weekDays[0]?.ymd;
      const weekLast = weekDays[weekDays.length - 1]?.ymd;
      if (!weekFirst || !weekLast) return false;
      // entry overlaps week if entry.start <= weekLast AND (entry.end ?? ∞) >= weekFirst
      if (startYMD > weekLast) return false;
      if (endYMD !== null && endYMD < weekFirst) return false;
      return true;
    });
  }, [shouldShow, entries, tz, weekDays]);

  // ---- Actions -------------------------------------------------------------------

  async function confirm() {
    setBusy(true);
    try {
      await httpsCallable(functions, 'confirmWeeklyShifts')({ weekStartDate: thisMondayYMD });
      setHidden(true);
    } catch {
      // Silent fail — onSnapshot will eventually reflect the saved state if the call did succeed.
      setHidden(true);
    } finally {
      setBusy(false);
    }
  }

  function editShifts() {
    setHidden(true);
    navigate('/responder/availability');
  }

  // ---- Render --------------------------------------------------------------------

  if (!shouldShow) return null;

  return (
    <div className="wcp-backdrop" role="dialog" aria-modal="true" aria-label="Confirm your shifts">
      <div className="wcp-modal">
        <div className="wcp-header">
          <div className="wcp-eyebrow">Monday check-in</div>
          <h3>Confirm your shifts this week</h3>
        </div>

        <div className="wcp-week">
          {weekDays.map((d) => (
            <div key={d.ymd} className={`wcp-day wcp-day-${cellClass(d)}`}>
              <div className="wcp-day-name">{d.dayName}</div>
              <div className="wcp-day-num">{d.dayNum}</div>
            </div>
          ))}
        </div>

        <div className="wcp-summary">
          {weekEntries.length === 0 ? (
            <div className="wcp-summary-empty">
              {availabilityModel === 'opt-out'
                ? "No specific shifts set — you're treated as on-call by default this week."
                : "No shifts set for this week — you'll be off-call. Edit if that's wrong."}
            </div>
          ) : (
            <ul className="wcp-summary-list">
              {weekEntries.map((entry) => (
                <li key={entry.id} className={`wcp-summary-item wcp-summary-${entry.status}`}>
                  {describeEntry(entry, tz)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="wcp-actions">
          <button className="btn-ghost" onClick={editShifts} disabled={busy}>
            Edit my shifts
          </button>
          <button className="btn-primary" onClick={confirm} disabled={busy}>
            {busy ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------------------

function cellClass(d) {
  if (d.status === 'unavailable') return 'unavailable';
  if (d.status === 'available') return 'available';
  if (d.implicitlyAvailable) return 'implicit';
  return 'off';
}

/**
 * 0..6 (Sun..Sat) for a YYYY-MM-DD string. Used to detect Monday.
 * We compute in local time — close enough since the only consumer is the
 * "is today Monday" check, and a user's "today" semantics match local-clock perception.
 */
function dayOfWeekFromYMD(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getDay();
}
